import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Container,
  Button,
  Spinner,
  Alert,
  Form,
  Modal,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { useLanguage } from "../context/LanguageContext.js";
import { callXeroFunction, auth } from "../firebase.js";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { format } from "date-fns";
import { useGoogleMaps } from "../hooks/useGoogleMaps.js";
import "./CalendarPage.css"; // Importar estilos do CalendarPage

export default function PersonalAgendaPage() {
  const { language } = useLanguage();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Clients for autocomplete
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Google Maps address autocomplete
  const { addressService, aucklandBounds, isLoaded } = useGoogleMaps();
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressDebounceRef = useRef(null);
  const addressSessionTokenRef = useRef(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_date: "",
    event_time: "",
    event_end_time: "", // Optional end time
    address: "",
    client_id: null,
    client_name: "",
    reminder_config: [120, 15], // Default: 2h and 15min before
    recurrence_type: null, // 'daily' | 'weekly' | 'biweekly' | 'monthly' | null
    recurrence_end_date: null, // Optional end date for recurrence
  });

  // Generate time options (06:00 to 22:00, 15-min intervals)
  const timeOptions = useMemo(() => {
    const options = [];
    for (let h = 6; h <= 22; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (h === 22 && m > 0) break; // Stop at 22:00
        const hour = String(h).padStart(2, '0');
        const min = String(m).padStart(2, '0');
        options.push(`${hour}:${min}`);
      }
    }
    return options;
  }, []);

  // Format date as DD/MM/YYYY for display
  const formatDateDisplay = useCallback((dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }, []);

  // Reminder input state
  const [reminderInput, setReminderInput] = useState("01:00");

  // Delete modal for recurring events
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const t = {
    pt: {
      title: "Minha Agenda",
      subtitle: "Clique no calendário para adicionar eventos. Configure lembretes personalizados via WhatsApp.",
      eventTitle: "Título",
      description: "Descrição",
      date: "Data",
      time: "Horário",
      startTime: "Início",
      endTime: "Término",
      selectTime: "Selecione",
      address: "Endereço",
      client: "Cliente",
      selectClient: "Buscar cliente...",
      noClient: "Sem cliente (evento pessoal)",
      save: "Salvar",
      cancel: "Cancelar",
      delete: "Excluir",
      createEvent: "Novo Evento",
      editEvent: "Editar Evento",
      confirmDelete: "Excluir este evento?",
      required: "Obrigatório",
      optional: "Opcional",
      reminderSent: "Lembrete enviado",
      reminderPending: "Lembrete pendente",
      clearClient: "Limpar cliente",
      reminders: "Lembretes WhatsApp",
      addReminder: "Adicionar",
      remindBefore: "antes",
      noReminders: "Sem lembretes configurados",
      duplicateReminder: "Este lembrete já existe",
      invalidFormat: "Formato inválido (use HH:MM)",
      // Recurrence
      repeat: "Repetir",
      noRepeat: "Não repetir",
      daily: "Diariamente",
      weekly: "Semanalmente",
      biweekly: "A cada 14 dias",
      monthly: "Mensalmente",
      repeatUntil: "Repetir até",
      repeatUntilHint: "Padrão: 1 ano a partir da data inicial",
      // Delete modal
      deleteRecurringTitle: "Excluir evento recorrente",
      deleteRecurringMessage: "Este evento faz parte de uma série recorrente. O que deseja fazer?",
      deleteOnlyThis: "Excluir apenas este evento",
      deleteThisAndFuture: "Excluir este e todos os futuros",
    },
    en: {
      title: "My Agenda",
      subtitle: "Click on the calendar to add events. Configure custom WhatsApp reminders.",
      eventTitle: "Title",
      description: "Description",
      date: "Date",
      time: "Time",
      startTime: "Start",
      endTime: "End",
      selectTime: "Select",
      address: "Address",
      client: "Client",
      selectClient: "Search client...",
      noClient: "No client (personal event)",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      createEvent: "New Event",
      editEvent: "Edit Event",
      confirmDelete: "Delete this event?",
      required: "Required",
      optional: "Optional",
      reminderSent: "Reminder sent",
      reminderPending: "Reminder pending",
      clearClient: "Clear client",
      reminders: "WhatsApp Reminders",
      addReminder: "Add",
      remindBefore: "before",
      noReminders: "No reminders configured",
      duplicateReminder: "This reminder already exists",
      invalidFormat: "Invalid format (use HH:MM)",
      // Recurrence
      repeat: "Repeat",
      noRepeat: "Don't repeat",
      daily: "Daily",
      weekly: "Weekly",
      biweekly: "Every 14 days",
      monthly: "Monthly",
      repeatUntil: "Repeat until",
      repeatUntilHint: "Default: 1 year from start date",
      // Delete modal
      deleteRecurringTitle: "Delete recurring event",
      deleteRecurringMessage: "This event is part of a recurring series. What would you like to do?",
      deleteOnlyThis: "Delete only this event",
      deleteThisAndFuture: "Delete this and all future events",
    },
  };

  const txt = t[language] || t.en;

  // Helper functions for HH:MM <-> minutes conversion
  const hhmmToMinutes = useCallback((hhmm) => {
    const parts = hhmm.split(':');
    if (parts.length !== 2) return null;
    const hh = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    if (isNaN(hh) || isNaN(mm) || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  }, []);

  const minutesToHHMM = useCallback((mins) => {
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }, []);

  const minutesToFriendlyText = useCallback((mins) => {
    if (mins < 60) {
      return `${mins} ${mins === 1 ? (language === 'pt' ? 'minuto' : 'minute') : (language === 'pt' ? 'minutos' : 'minutes')}`;
    } else if (mins === 60) {
      return language === 'pt' ? '1 hora' : '1 hour';
    } else if (mins < 1440) {
      const hours = Math.floor(mins / 60);
      const minutes = mins % 60;
      if (minutes === 0) {
        return `${hours} ${language === 'pt' ? 'horas' : 'hours'}`;
      }
      return `${hours}h${minutes}min`;
    } else if (mins === 1440) {
      return language === 'pt' ? '1 dia' : '1 day';
    } else {
      const days = Math.floor(mins / 1440);
      return `${days} ${language === 'pt' ? 'dias' : 'days'}`;
    }
  }, [language]);

  // Add reminder to list
  const handleAddReminder = useCallback(() => {
    const minutes = hhmmToMinutes(reminderInput);
    if (minutes === null || minutes < 1) {
      alert(txt.invalidFormat);
      return;
    }
    if (formData.reminder_config.includes(minutes)) {
      alert(txt.duplicateReminder);
      return;
    }
    setFormData(prev => ({
      ...prev,
      reminder_config: [...prev.reminder_config, minutes].sort((a, b) => b - a)
    }));
    setReminderInput("01:00");
  }, [reminderInput, formData.reminder_config, hhmmToMinutes, txt]);

  // Remove reminder from list
  const handleRemoveReminder = useCallback((minutes) => {
    setFormData(prev => ({
      ...prev,
      reminder_config: prev.reminder_config.filter(m => m !== minutes)
    }));
  }, []);

  // Load clients for autocomplete
  useEffect(() => {
    const loadClients = async () => {
      try {
        const result = await callXeroFunction("getClients", {}, { method: "GET" });
        if (result.clients) {
          setClients(result.clients);
        }
      } catch (err) {
        console.error("Error loading clients:", err);
      }
    };
    loadClients();
  }, []);

  // Filtered clients based on search
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return [];
    const search = clientSearch.toLowerCase();
    return clients
      .filter(c => c.name?.toLowerCase().includes(search))
      .slice(0, 10); // Limit to 10 results
  }, [clients, clientSearch]);

  // Convert event data to FullCalendar format
  const convertToCalendarEvent = useCallback((event) => {
    const baseEvent = {
      id: String(event.id),
      title: event.title,
      start: `${event.event_date.split('T')[0]}T${event.event_time}`,
      backgroundColor: "#007bff",
      borderColor: "#007bff",
      extendedProps: {
        ...event,
        description: event.description,
        address: event.address,
        reminder_sent: event.reminder_sent,
        client_id: event.client_id,
        client_name: event.client_name,
        recurrence_type: event.recurrence_type,
        recurrence_group_id: event.recurrence_group_id,
        recurrence_end_date: event.recurrence_end_date,
        event_end_time: event.event_end_time,
      }
    };
    // Add end time if exists
    if (event.event_end_time) {
      baseEvent.end = `${event.event_date.split('T')[0]}T${event.event_end_time}`;
    }
    return baseEvent;
  }, []);

  // Load events (only on initial mount)
  const loadEvents = useCallback(async (showLoadingSpinner = true) => {
    try {
      if (showLoadingSpinner) setLoading(true);
      setError(null);

      const result = await callXeroFunction("getPersonalEvents", {}, { method: "GET" });

      if (result.events) {
        const calendarEvents = result.events.map(convertToCalendarEvent);
        setEvents(calendarEvents);
      }
    } catch (err) {
      console.error("Error loading events:", err);
      setError(err.message);
    } finally {
      if (showLoadingSpinner) setLoading(false);
    }
  }, [convertToCalendarEvent]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Round time to nearest 15-minute interval
  const roundToNearest15 = useCallback((timeStr) => {
    if (!timeStr) return '09:00';
    const [h, m] = timeStr.split(':').map(Number);
    const roundedM = Math.round(m / 15) * 15;
    const finalH = roundedM === 60 ? h + 1 : h;
    const finalM = roundedM === 60 ? 0 : roundedM;
    return `${String(finalH).padStart(2, '0')}:${String(finalM).padStart(2, '0')}`;
  }, []);

  // Handle click on empty slot (create new event)
  const handleDateSelect = useCallback((selectInfo) => {
    const startDate = selectInfo.start;
    const startTime = roundToNearest15(format(startDate, "HH:mm"));

    setFormData({
      title: "",
      description: "",
      event_date: format(startDate, "yyyy-MM-dd"),
      event_time: startTime,
      event_end_time: "",
      address: "",
      client_id: null,
      client_name: "",
      reminder_config: [120, 15], // Default: 2h and 15min before
      recurrence_type: null,
      recurrence_end_date: null,
    });
    setClientSearch("");
    setReminderInput("01:00");
    setModalMode("create");
    setSelectedEvent(null);
    setShowModal(true);

    // Clear selection
    if (calendarRef.current) {
      calendarRef.current.getApi().unselect();
    }
  }, [roundToNearest15]);

  // Handle create new event button (uses current date/time)
  const handleCreateNewEvent = useCallback(() => {
    const now = new Date();
    // Round to next 15 minutes
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;
    now.setMinutes(roundedMinutes, 0, 0);

    setFormData({
      title: "",
      description: "",
      event_date: format(now, "yyyy-MM-dd"),
      event_time: format(now, "HH:mm"),
      event_end_time: "",
      address: "",
      client_id: null,
      client_name: "",
      reminder_config: [120, 15],
      recurrence_type: null,
      recurrence_end_date: null,
    });
    setClientSearch("");
    setReminderInput("01:00");
    setModalMode("create");
    setSelectedEvent(null);
    setShowModal(true);
  }, []);

  // Handle click on existing event (edit)
  const handleEventClick = useCallback((clickInfo) => {
    const event = clickInfo.event;
    const props = event.extendedProps;

    // Parse reminder_config from backend (might be string or array)
    let reminderConfig = [120, 15]; // Default
    if (props.reminder_config) {
      try {
        reminderConfig = typeof props.reminder_config === 'string'
          ? JSON.parse(props.reminder_config)
          : props.reminder_config;
      } catch (e) {
        console.warn('Failed to parse reminder_config:', e);
      }
    }

    setFormData({
      title: event.title,
      description: props.description || "",
      event_date: props.event_date.split("T")[0],
      event_time: props.event_time.substring(0, 5),
      event_end_time: props.event_end_time ? props.event_end_time.substring(0, 5) : "",
      address: props.address || "",
      client_id: props.client_id || null,
      client_name: props.client_name || "",
      reminder_config: reminderConfig,
      recurrence_type: props.recurrence_type || null,
      recurrence_end_date: props.recurrence_end_date ? props.recurrence_end_date.split("T")[0] : null,
    });
    setClientSearch(props.client_name || "");
    setReminderInput("01:00");
    setModalMode("edit");
    setSelectedEvent({
      id: event.id,
      ...props
    });
    setShowModal(true);
  }, []);

  // Select client from dropdown
  const handleSelectClient = useCallback((client) => {
    setFormData(prev => ({
      ...prev,
      client_id: client.id,
      client_name: client.name,
      address: client.address || prev.address, // Auto-fill address if client has one
    }));
    setClientSearch(client.name);
    setShowClientDropdown(false);
  }, []);

  // Clear selected client
  const handleClearClient = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      client_id: null,
      client_name: "",
    }));
    setClientSearch("");
  }, []);

  // Handle address autocomplete (Google Maps) - com debounce 300ms + session token
  const handleAddressChange = useCallback((val) => {
    setFormData(prev => ({ ...prev, address: val }));

    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);

    if (addressService && val && val.length >= 3 && isLoaded && window.google) {
      if (!addressSessionTokenRef.current && window.google?.maps?.places) {
        addressSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
      addressDebounceRef.current = setTimeout(() => {
        addressService.getPlacePredictions(
          {
            input: val,
            componentRestrictions: { country: "nz" },
            locationRestriction: aucklandBounds,
            sessionToken: addressSessionTokenRef.current
          },
          (preds, status) => {
            setAddressSuggestions(status === "OK" ? preds : []);
          }
        );
      }, 300);
    } else {
      setAddressSuggestions([]);
    }
  }, [addressService, aucklandBounds, isLoaded]);

  // Save event with optimistic update
  const handleSave = useCallback(async () => {
    try {
      setActionLoading(true);

      const eventData = {
        ...formData,
        client_id: formData.client_id || null,
        client_name: formData.client_name || null,
        recurrence_type: formData.recurrence_type || null,
        recurrence_end_date: formData.recurrence_end_date || null,
      };

      if (modalMode === "create") {
        // Optimistic: add temporary event(s)
        const tempId = `temp-${Date.now()}`;
        const tempEvent = {
          id: tempId,
          title: formData.title,
          start: `${formData.event_date}T${formData.event_time}`,
          backgroundColor: "#007bff",
          borderColor: "#007bff",
          extendedProps: {
            ...formData,
            event_date: formData.event_date,
            event_time: formData.event_time,
            reminder_sent: false,
          }
        };
        setEvents(prev => [...prev, tempEvent]);
        setShowModal(false);

        // Send to backend
        const result = await callXeroFunction("createPersonalEvent", eventData, { method: "POST" });

        // Handle response - could be single event or multiple events for recurrence
        if (result.events && result.events.length > 0) {
          // Multiple events (recurrence) - replace temp with all real events
          setEvents(prev => {
            const withoutTemp = prev.filter(e => e.id !== tempId);
            const newEvents = result.events.map(convertToCalendarEvent);
            return [...withoutTemp, ...newEvents];
          });
        } else if (result.event) {
          // Single event - replace temp with real event
          setEvents(prev => prev.map(e =>
            e.id === tempId ? convertToCalendarEvent(result.event) : e
          ));
        }
      } else {
        // Optimistic: update existing event
        setEvents(prev => prev.map(e =>
          e.id === selectedEvent.id
            ? {
                ...e,
                title: formData.title,
                start: `${formData.event_date}T${formData.event_time}`,
                extendedProps: {
                  ...e.extendedProps,
                  ...formData,
                  event_date: formData.event_date,
                  event_time: formData.event_time,
                }
              }
            : e
        ));
        setShowModal(false);

        // Send to backend
        await callXeroFunction("updatePersonalEvent", eventData, {
          method: "PUT",
          id: selectedEvent.id,
        });
      }
    } catch (err) {
      console.error("Error saving event:", err);
      setError(err.message);
      // Revert on error
      loadEvents(false);
    } finally {
      setActionLoading(false);
    }
  }, [formData, modalMode, selectedEvent, convertToCalendarEvent, loadEvents]);

  // Handle delete button click - shows modal for recurring events
  const handleDelete = useCallback(() => {
    // If it's a recurring event, show the delete options modal
    if (selectedEvent?.recurrence_group_id) {
      setShowDeleteModal(true);
      return;
    }

    // Single event - confirm and delete
    if (window.confirm(txt.confirmDelete)) {
      handleDeleteConfirm('single');
    }
  }, [selectedEvent, txt.confirmDelete]);

  // Confirm delete with mode (single or future)
  const handleDeleteConfirm = useCallback(async (mode) => {
    // Save scroll position before closing modal
    const scrollPos = window.scrollY;

    try {
      setActionLoading(true);
      setShowDeleteModal(false);
      setShowModal(false);

      const deletedEventId = selectedEvent.id;
      const recurrenceGroupId = selectedEvent.recurrence_group_id;
      const eventDate = selectedEvent.event_date?.split('T')[0];

      // Optimistic update
      if (mode === 'future' && recurrenceGroupId) {
        // Remove all events from the same group with date >= current event date
        setEvents(prev => prev.filter(e => {
          if (e.extendedProps.recurrence_group_id !== recurrenceGroupId) return true;
          const eDate = e.extendedProps.event_date?.split('T')[0];
          return eDate < eventDate;
        }));
      } else {
        // Remove only this event
        setEvents(prev => prev.filter(e => e.id !== deletedEventId));
      }

      // Restore scroll position after state update
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPos);
      });

      // Send to backend with mode query param
      await callXeroFunction("deletePersonalEvent", {}, {
        method: "DELETE",
        id: deletedEventId,
        queryParams: { mode },
      });
    } catch (err) {
      console.error("Error deleting event:", err);
      setError(err.message);
      // Revert on error
      loadEvents(false);
    } finally {
      setActionLoading(false);
    }
  }, [selectedEvent, loadEvents]);

  // Handle event drag & drop with optimistic update
  const handleEventDrop = useCallback(async (dropInfo) => {
    const event = dropInfo.event;
    const newStart = event.start;
    const newDate = format(newStart, "yyyy-MM-dd");
    const newTime = format(newStart, "HH:mm");

    // Optimistic update already happened via FullCalendar
    // Update our state to match
    setEvents(prev => prev.map(e =>
      e.id === event.id
        ? {
            ...e,
            start: `${newDate}T${newTime}`,
            extendedProps: {
              ...e.extendedProps,
              event_date: newDate,
              event_time: newTime,
            }
          }
        : e
    ));

    try {
      await callXeroFunction("updatePersonalEvent", {
        title: event.title,
        description: event.extendedProps.description || "",
        event_date: newDate,
        event_time: newTime,
        address: event.extendedProps.address || "",
        client_id: event.extendedProps.client_id || null,
        client_name: event.extendedProps.client_name || null,
        reset_reminders: true, // Reset reminders when moving event
      }, {
        method: "PUT",
        id: event.id,
      });
    } catch (err) {
      console.error("Error moving event:", err);
      setError(err.message);
      dropInfo.revert();
      // Reload to get correct state
      loadEvents(false);
    }
  }, [loadEvents]);

  // Memoized event content renderer
  const renderEventContent = useCallback((eventInfo) => {
    const props = eventInfo.event.extendedProps;
    return (
      <div className="p-1" style={{ overflow: 'hidden' }}>
        <div className="fw-bold" style={{ fontSize: '0.85rem' }}>
          {props.recurrence_type && <span title="Evento recorrente">🔄 </span>}
          {eventInfo.event.title}
        </div>
        {props.client_name && (
          <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
            👤 {props.client_name}
          </div>
        )}
        {props.address && (
          <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
            📍 {props.address.substring(0, 20)}{props.address.length > 20 ? '...' : ''}
          </div>
        )}
      </div>
    );
  }, []);

  // Custom day header content for DD/MM format
  const dayHeaderContent = useCallback((arg) => {
    const date = arg.date;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    return `${weekday} ${day}/${month}`;
  }, []);

  // Memoized calendar options
  const calendarOptions = useMemo(() => ({
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    initialView: isMobile ? "timeGridDay" : "timeGridWeek",
    headerToolbar: {
      left: "prev,next today",
      center: "title",
      // Mobile: only day and list | Desktop: all views
      right: isMobile ? "timeGridDay,listWeek" : "dayGridMonth,timeGridWeek,timeGridDay,listWeek"
    },
    firstDay: 1,
    slotMinTime: "06:00:00",
    slotMaxTime: "22:00:00",
    snapDuration: "00:15:00",
    allDaySlot: false,
    selectable: true,
    selectMirror: true,
    editable: true,
    height: "auto",
    // Mobile touch support
    selectLongPressDelay: 0, // Permite clique imediato sem long press
    longPressDelay: 300, // Para drag de eventos
    eventLongPressDelay: 300, // Para arrastar eventos existentes
    // Custom day header for DD/MM format
    dayHeaderContent: dayHeaderContent,
    eventTimeFormat: {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    },
    slotLabelFormat: {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }
  }), [isMobile, dayHeaderContent]);

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-2 px-md-4">
      {/* Header */}
      <div className="mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h2 className="mb-1">🗓️ {txt.title}</h2>
          <p className="text-muted mb-0">
            📱 {txt.subtitle}
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleCreateNewEvent}
          className="d-flex align-items-center gap-2"
        >
          ➕ {language === "pt" ? "Criar Evento" : "Create Event"}
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Calendar with dark theme */}
      <div className="calendar-container light-theme">
        <FullCalendar
          ref={calendarRef}
          {...calendarOptions}
          events={events}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          select={handleDateSelect}
          eventDrop={handleEventDrop}
        />
      </div>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "create" ? `➕ ${txt.createEvent}` : `✏️ ${txt.editEvent}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {txt.eventTitle} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    required
                    autoFocus
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    📅 {txt.date} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.event_date}
                    onChange={(e) =>
                      setFormData({ ...formData, event_date: e.target.value })
                    }
                    required
                  />
                  {formData.event_date && (
                    <Form.Text className="text-primary fw-bold">
                      {formatDateDisplay(formData.event_date)}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    🕐 {txt.startTime} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.event_time}
                    onChange={(e) =>
                      setFormData({ ...formData, event_time: e.target.value })
                    }
                    required
                  >
                    <option value="">{txt.selectTime}</option>
                    {timeOptions.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    🕑 {txt.endTime} <span className="text-muted">({txt.optional})</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.event_end_time}
                    onChange={(e) =>
                      setFormData({ ...formData, event_end_time: e.target.value })
                    }
                  >
                    <option value="">-</option>
                    {timeOptions
                      .filter(time => !formData.event_time || time > formData.event_time)
                      .map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Client Selection with Autocomplete */}
            <Form.Group className="mb-3">
              <Form.Label>
                👤 {txt.client} <span className="text-muted">({txt.optional})</span>
              </Form.Label>
              <div className="position-relative">
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setShowClientDropdown(true);
                      // Clear client selection if user types
                      if (formData.client_id && e.target.value !== formData.client_name) {
                        setFormData(prev => ({
                          ...prev,
                          client_id: null,
                          client_name: "",
                        }));
                      }
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    className="flex-grow-1"
                  />
                  {formData.client_id && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleClearClient}
                      title={txt.clearClient}
                    >
                      ✕
                    </Button>
                  )}
                </div>

                {/* Client dropdown */}
                {showClientDropdown && filteredClients.length > 0 && (
                  <div
                    className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                    style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto' }}
                  >
                    {filteredClients.map(client => (
                      <div
                        key={client.id}
                        className="p-2 cursor-pointer hover-bg-light"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelectClient(client)}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        <div className="fw-bold">{client.name}</div>
                        {client.address && (
                          <small className="text-muted">{client.address}</small>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Show selected client badge */}
                {formData.client_id && (
                  <div className="mt-2">
                    <Badge bg="info" className="d-inline-flex align-items-center gap-1">
                      <span>👤 {formData.client_name}</span>
                    </Badge>
                  </div>
                )}
              </div>
            </Form.Group>

            <Form.Group className="mb-3 position-relative">
              <Form.Label>
                📍 {txt.address} <span className="text-muted">({txt.optional})</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.address}
                onChange={(e) => handleAddressChange(e.target.value)}
              />
              {/* Address suggestions dropdown */}
              {addressSuggestions.length > 0 && (
                <ul
                  className="list-group position-absolute w-100 mt-1 shadow-sm"
                  style={{ zIndex: 1051, maxHeight: "200px", overflowY: "auto" }}
                >
                  {addressSuggestions.map((pred) => (
                    <li
                      key={pred.place_id}
                      className="list-group-item list-group-item-action"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, address: pred.description }));
                        setAddressSuggestions([]);
                        addressSessionTokenRef.current = null;
                      }}
                    >
                      {pred.description}
                    </li>
                  ))}
                </ul>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                📝 {txt.description} <span className="text-muted">({txt.optional})</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Form.Group>

            {/* Recurrence Configuration */}
            <Form.Group className="mb-3">
              <Form.Label>🔄 {txt.repeat}</Form.Label>
              <Form.Select
                value={formData.recurrence_type || ""}
                onChange={(e) =>
                  setFormData(prev => ({
                    ...prev,
                    recurrence_type: e.target.value || null,
                  }))
                }
              >
                <option value="">{txt.noRepeat}</option>
                <option value="daily">{txt.daily}</option>
                <option value="weekly">{txt.weekly}</option>
                <option value="biweekly">{txt.biweekly}</option>
                <option value="monthly">{txt.monthly}</option>
              </Form.Select>
            </Form.Group>

            {formData.recurrence_type && (
              <Form.Group className="mb-3">
                <Form.Label>
                  📅 {txt.repeatUntil} <span className="text-muted">({txt.optional})</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={formData.recurrence_end_date || ""}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      recurrence_end_date: e.target.value || null,
                    }))
                  }
                  min={formData.event_date}
                />
                <Form.Text className="text-muted">
                  {txt.repeatUntilHint}
                </Form.Text>
              </Form.Group>
            )}

            {/* WhatsApp Reminders Configuration */}
            <Form.Group className="mb-3">
              <Form.Label>
                🔔 {txt.reminders}
              </Form.Label>

              {/* Add new reminder */}
              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  type="text"
                  value={reminderInput}
                  onChange={(e) => {
                    // Allow only digits and colon
                    const val = e.target.value.replace(/[^\d:]/g, '');
                    if (val.length <= 5) setReminderInput(val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddReminder();
                    }
                  }}
                  style={{ maxWidth: '100px' }}
                />
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleAddReminder}
                >
                  + {txt.addReminder}
                </Button>
              </div>

              {/* List of configured reminders */}
              <div className="d-flex flex-wrap gap-2">
                {formData.reminder_config.length === 0 ? (
                  <span className="text-muted small">{txt.noReminders}</span>
                ) : (
                  formData.reminder_config.map((minutes) => (
                    <Badge
                      key={minutes}
                      bg="info"
                      className="d-flex align-items-center gap-1 py-2 px-2"
                      style={{ fontSize: '0.85rem' }}
                    >
                      ⏰ {minutesToHHMM(minutes)} ({minutesToFriendlyText(minutes)} {txt.remindBefore})
                      <span
                        style={{ cursor: 'pointer', marginLeft: '4px' }}
                        onClick={() => handleRemoveReminder(minutes)}
                        title={language === 'pt' ? 'Remover' : 'Remove'}
                      >
                        ✕
                      </span>
                    </Badge>
                  ))
                )}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div className="d-flex gap-2">
            {modalMode === "edit" && (
              <>
                <Button
                  variant="outline-danger"
                  onClick={handleDelete}
                  disabled={actionLoading}
                >
                  🗑️ {txt.delete}
                </Button>
              </>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {txt.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!formData.title || !formData.event_date || !formData.event_time || actionLoading}
            >
              {actionLoading ? <Spinner size="sm" /> : `✅ ${txt.save}`}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Delete Recurring Event Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>🗑️ {txt.deleteRecurringTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{txt.deleteRecurringMessage}</p>

          <div className="d-grid gap-2">
            <Button
              variant="outline-danger"
              onClick={() => handleDeleteConfirm('single')}
              disabled={actionLoading}
            >
              {txt.deleteOnlyThis}
            </Button>
            <Button
              variant="danger"
              onClick={() => handleDeleteConfirm('future')}
              disabled={actionLoading}
            >
              {txt.deleteThisAndFuture}
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {txt.cancel}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Click outside to close dropdown */}
      {showClientDropdown && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 1040 }}
          onClick={() => setShowClientDropdown(false)}
        />
      )}
    </Container>
  );
}
